import { seeThrough } from 'styles/theme/colors';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useHelp } from 'shared/context/HelpContext';
import { useIsResizing } from '../../hooks/useIsResizing';
import {
  buttonClasses,
  helpCardClasses,
} from '../../../styles/tailwindClassnames';
import {
  trackHelpPanelOpened,
  trackHelpArticleClicked,
  trackHelpSupportCenterClicked,
  trackHelpContactClicked,
} from 'shared/analytics/analytics';
import { Icon } from 'react-icons-kit';
import { ic_close } from 'react-icons-kit/md/ic_close';

const SUPPORT_CENTER_URL = 'https://support.earthranger.com/en_US/gundi';

const HelpSidePanel: React.FC = () => {
  const { t } = useTranslation();
  const {
    helpContent,
    isPanelOpen,
    openPanel,
    closePanel,
    rightOffsetPx,
    fabSuppressed,
  } = useHelp();
  const [buttonVisible, setButtonVisible] = useState(false);
  // While resizing, drop the slide transition so the width switch at the md breakpoint
  // doesn't animate the closed (translate-x-full) panel across the viewport.
  const isResizing = useIsResizing();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => setButtonVisible(true), 350);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    closePanel();
  }, [location.pathname, closePanel]);

  return (
    <>
      {/* Floating ? button */}
      {!isPanelOpen && !fabSuppressed && (
        <button
          onClick={() => {
            trackHelpPanelOpened();
            openPanel();
          }}
          className={`fixed bottom-6 z-[57] flex h-12 w-12 items-center justify-center rounded-full backdrop-blur-[1.5px] transition-all duration-[600ms] hover:opacity-90 md:z-[55] ${buttonVisible ? 'translate-y-0 scale-100 opacity-100 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]' : 'translate-y-8 scale-0 opacity-0'}`}
          style={{
            backgroundColor: seeThrough('white', 0.8),
            right: 24 + rightOffsetPx,
            transition: 'right 300ms, opacity 600ms, transform 600ms',
          }}
          aria-label={t('Open Help Panel')}
          data-testid="HelpSidePanel-floating-button"
        >
          <span className="text-heading-sm font-bold leading-none text-ui-cta-primary">
            ?
          </span>
        </button>
      )}

      {/* Side panel — full-screen below md (stacks on top of a full-screen Rules panel,
                z-58 > Rules z-56); fixed-width and side-by-side at md+. */}
      <div
        className={`fixed top-0 z-[58] flex h-full w-full flex-col bg-white shadow-xl transition-transform duration-300 md:z-[60] md:w-[383px] ${isPanelOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{
          right: rightOffsetPx,
          transition: isResizing ? 'none' : 'transform 300ms, right 300ms',
        }}
        data-testid="HelpSidePanel-panel"
        aria-hidden={!isPanelOpen}
        {...(!isPanelOpen &&
          ({ inert: '' } as unknown as React.HTMLAttributes<HTMLDivElement>))}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4">
          <a
            href={SUPPORT_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackHelpSupportCenterClicked}
            className={buttonClasses.outlinePrimaryLink}
            data-testid="HelpSidePanel-support-center-button"
          >
            {t('Support Center')}
          </a>
          <button
            onClick={closePanel}
            className="flex h-9 w-9 items-center justify-center text-ui-text-secondary-text hover:text-ui-text-primary-text"
            aria-label={t('Close')}
            data-testid="HelpSidePanel-close-button"
          >
            <Icon icon={ic_close} size={24} />
          </button>
        </div>
        <hr className="mt-[12px] border-t border-ui-divider-lines" />

        {/* Articles */}
        <div className="flex-1 overflow-y-auto px-4 pb-6">
          {helpContent ? (
            <>
              <h2 className="mb-3 mt-[28px] text-heading-md font-normal leading-normal text-ui-text-primary-text">
                {t('Help Center Articles')}
              </h2>
              <div className="flex flex-col gap-4">
                {helpContent.cards.map((card) => (
                  <div key={card.button.url} className={helpCardClasses.card}>
                    <h3 className="self-stretch text-body-lg font-normal leading-normal text-ui-cta-primary">
                      {card.title}
                    </h3>
                    <p className="self-stretch text-body-md font-normal leading-normal text-ui-text-primary-text">
                      {card.description}
                    </p>
                    <a
                      href={card.button.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackHelpArticleClicked(card.title, card.button.url)
                      }
                      className={`${buttonClasses.primaryAction} ${helpCardClasses.button}`}
                    >
                      {card.button.label}
                    </a>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="mb-3 mt-[28px] text-heading-md font-normal leading-normal text-ui-text-primary-text">
                {t('Get Support')}
              </h2>
              <div className="flex flex-col gap-4">
                <div className={helpCardClasses.card}>
                  <h3 className="self-stretch text-body-lg font-normal leading-normal text-ui-cta-primary">
                    {t('Support Center')}
                  </h3>
                  <p className="self-stretch text-body-md font-normal leading-normal text-ui-text-primary-text">
                    {t('Browse the Support Center for guides and resources.')}
                  </p>
                  <a
                    href="https://support.earthranger.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackHelpSupportCenterClicked}
                    className={`${buttonClasses.primaryAction} ${helpCardClasses.button}`}
                  >
                    {t('Visit')}
                  </a>
                </div>
                <div className={helpCardClasses.card}>
                  <h3 className="self-stretch text-body-lg font-normal leading-normal text-ui-cta-primary">
                    {t('Contact Support Team')}
                  </h3>
                  <p className="self-stretch text-body-md font-normal leading-normal text-ui-text-primary-text">
                    {t(
                      "Have a question or need help with your setup? Reach out to our team and we'll get back to you as soon as possible.",
                    )}
                  </p>
                  <a
                    href="https://support.earthranger.com/en_US/gundi-getting-help/gundi-contact-us"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackHelpContactClicked}
                    className={`${buttonClasses.primaryAction} ${helpCardClasses.button}`}
                  >
                    {t('Contact')}
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
        {/* Footer links */}
        <div className="flex flex-col gap-2 border-t border-ui-divider-lines px-4 py-4">
          <a
            href="https://projectgundi.org/Legal-Pages/User-Agreement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-sm text-ui-cta-primary hover:underline"
          >
            {t('Gundi EULA')}
          </a>
        </div>
      </div>
    </>
  );
};

export default HelpSidePanel;
